import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

const STORAGE_KEY = 'client-account-table-column-widths';

type WidthMap = Record<string, number>;
type TablesWidths = Record<string, WidthMap>;

function loadAll(): TablesWidths {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveAll(all: TablesWidths) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // ignore quota / private mode
  }
}

export function useResizableTableColumns(
  tableId: string,
  defaultWidths: WidthMap
) {
  const [widths, setWidths] = useState<WidthMap>(() => {
    const all = loadAll();
    return { ...defaultWidths, ...(all[tableId] || {}) };
  });

  const dragRef = useRef<{
    key: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  useEffect(() => {
    const all = loadAll();
    all[tableId] = widths;
    saveAll(all);
  }, [tableId, widths]);

  const onResizeStart = useCallback(
    (key: string, e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {
        key,
        startX: e.clientX,
        startWidth: widths[key] ?? defaultWidths[key] ?? 100,
      };

      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const delta = ev.clientX - dragRef.current.startX;
        const next = Math.max(56, Math.round(dragRef.current.startWidth + delta));
        setWidths((prev) => ({ ...prev, [dragRef.current!.key]: next }));
      };

      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [widths, defaultWidths]
  );

  const getWidth = useCallback(
    (key: string) => widths[key] ?? defaultWidths[key] ?? 100,
    [widths, defaultWidths]
  );

  const resetWidths = useCallback(() => {
    setWidths({ ...defaultWidths });
  }, [defaultWidths]);

  return { widths, getWidth, onResizeStart, resetWidths };
}
